import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Table, {
  ColumnDefinitionType,
} from '../../../../../components/Table';
import MyModal from '../../../../../components/Modal';
import FormInput from '../../../../../components/Form/FormInput';
import FormSelectBox from '../../../../../components/Form/FormSelectBox';
import { Peligro, Fase } from '../../../../../types/AnalisisRiesgo';
import { PeligroDTO } from '../../../../../types/Peligro';
import { useAnalisisRiesgo } from '../../../../../hooks/useAnalisisRiesgo';
import { useRiesgos } from '../../../../../hooks/useRiesgos';
import '../EvaluacionRiesgosPersonales/styles.css';
import './styles.css';
import { logger } from '../../../../../utils/logger';

interface IdentificacionPeligrosEntornoProps {
  isDisabled?: boolean;
  isEditing?: boolean;
  peligroCheckStates?: {
    [faseId: number]: { [peligroId: number]: boolean };
  };
  setPeligroCheckStates?: React.Dispatch<
    React.SetStateAction<{
      [faseId: number]: { [peligroId: number]: boolean };
    }>
  >;
  observaciones?: string;
  setObservaciones?: React.Dispatch<React.SetStateAction<string>>;
  // Props del hook useAnalisisRiesgo
  fasesEntorno?: Fase[];
  setFasesEntorno?: React.Dispatch<React.SetStateAction<Fase[]>>;
  agregarFase?: (
    descripcion: string,
    tipo?: 'PERSONAL' | 'ENTORNO',
  ) => void;
  editarFase?: (
    id: number,
    nuevaDescripcion: string,
    tipo?: 'PERSONAL' | 'ENTORNO',
  ) => void;
  handlePeligroSelected?: (peligro: Peligro) => void;
  peligroSelected?: Peligro;
  eliminarFase?: (id: number, tipo?: 'PERSONAL' | 'ENTORNO') => void;
  // Función para exponer la validación al componente padre
  onValidationRef?: (validateFn: () => boolean) => void;
}

const IdentificacionPeligrosEntorno: React.FC<
  IdentificacionPeligrosEntornoProps
> = ({
  isDisabled = false,
  isEditing = true,
  peligroCheckStates: externalCheckStates,
  setPeligroCheckStates: externalSetCheckStates,
  observaciones: externalObservaciones,
  setObservaciones: externalSetObservaciones,
  // Props del hook
  fasesEntorno: propsFasesEntorno,
  setFasesEntorno: propsSetFasesEntorno,
  agregarFase: propsAgregarFase,
  editarFase: propsEditarFase,
  handlePeligroSelected: propsHandlePeligroSelected,
  peligroSelected: propsPeligroSelected,
  eliminarFase: propsEliminarFase,
  // Función de validación
  onValidationRef: propsOnValidationRef,
}) => {
  const [nuevaFase, setNuevaFase] = useState('');
  const [currentFaseId, setCurrentFaseId] = useState<number>();
  const [isConfirmDeleteFase, setIsConfirmDeleteFase] =
    useState(false);
  const [faseIdToDelete, setFaseIdToDelete] = useState<number>();
  const [isVisualizar, setIsVisualizar] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [peligrosEntorno, setPeligrosEntorno] = useState<
    PeligroDTO[]
  >([]);
  const [loadingPeligrosEntorno, setLoadingPeligrosEntorno] =
    useState(false);

  // Estados para el formulario de agregar peligro
  const [peligroFormData, setPeligroFormData] = useState({
    nombre: '',
    barreraAdministrativa: '',
    barreraFisica: '',
    eppEspecifico: '',
  });

  // Estado para manejar errores de validación
  const [formErrors, setFormErrors] = useState({
    nombre: '',
    barreraAdministrativa: '',
    barreraFisica: '',
    eppEspecifico: '',
  });

  // Estados específicos para el checkbox SI/NO - usar external o local
  const [localPeligroCheckStates, setLocalPeligroCheckStates] =
    useState<{
      [faseId: number]: { [peligroId: number]: boolean };
    }>({});

  // Estados para el modal de confirmación de limpieza de datos
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingCheckChange, setPendingCheckChange] = useState<{
    faseId: number;
    peligroId: number;
    value: boolean;
  } | null>(null);

  const peligroCheckStates =
    externalCheckStates || localPeligroCheckStates;
  const setPeligroCheckStates =
    externalSetCheckStates || setLocalPeligroCheckStates;

  // Estados para las observaciones - usar external o local
  const [localObservaciones, setLocalObservaciones] = useState('');

  const observaciones = externalObservaciones || localObservaciones;
  const setObservaciones =
    externalSetObservaciones || setLocalObservaciones;

  const {
    fasesEntorno: hookFasesEntorno,
    setFasesEntorno: hookSetFasesEntorno,
    peligroSelected: hookPeligroSelected,
    agregarFase: hookAgregarFase,
    editarFase: hookEditarFase,
    handlePeligroSelected: hookHandlePeligroSelected,
    eliminarFase: hookEliminarFase,
  } = useAnalisisRiesgo();

  // Usar props si están disponibles, sino usar el hook local
  const fasesEntorno = propsFasesEntorno || hookFasesEntorno;
  const setFasesEntorno = propsSetFasesEntorno || hookSetFasesEntorno;
  const peligroSelected = propsPeligroSelected || hookPeligroSelected;
  const agregarFase = propsAgregarFase || hookAgregarFase;
  const editarFase = propsEditarFase || hookEditarFase;
  const handlePeligroSelected =
    propsHandlePeligroSelected || hookHandlePeligroSelected;
  const eliminarFase = propsEliminarFase || hookEliminarFase;

  // Usar las fases de entorno específicamente
  const fases = fasesEntorno;

  const { obtenerPeligrosEntorno } = useRiesgos();

  // Cargar peligros de entorno al montar el componente
  useEffect(() => {
    const cargarPeligrosEntorno = async () => {
      setLoadingPeligrosEntorno(true);
      try {
        const peligros = await obtenerPeligrosEntorno();
        setPeligrosEntorno(peligros);
      } catch (error) {
        logger.error(
          'Error al cargar peligros de entorno:',
          logger.safeError(error),
        );
      } finally {
        setLoadingPeligrosEntorno(false);
      }
    };

    cargarPeligrosEntorno();
  }, [obtenerPeligrosEntorno]);

  // Inicializar checkboxes como "NO" por defecto para cada peligro en cada fase
  useEffect(() => {
    if (peligrosEntorno.length > 0 && fases.length > 0) {
      setPeligroCheckStates((prev) => {
        const newStates = { ...prev };

        fases.forEach((fase) => {
          if (!newStates[fase.id]) {
            newStates[fase.id] = {};
          }

          peligrosEntorno.forEach((peligro) => {
            // Solo inicializar como "NO" (false) si no tiene valor previo
            if (newStates[fase.id][peligro.id] === undefined) {
              newStates[fase.id][peligro.id] = false;
            }
          });
        });

        return newStates;
      });
    }
  }, [peligrosEntorno, fases, setPeligroCheckStates]);

  // Función para validar que los peligros seleccionados como "SI" tengan todas las barreras completas
  const validarPeligrosCompletos = React.useCallback((): boolean => {
    const errores: string[] = [];

    fases.forEach((fase) => {
      peligrosEntorno.forEach((peligroEntorno) => {
        // Verificar si el peligro está marcado como "SI" (true)
        const isSelected =
          peligroCheckStates[fase.id]?.[peligroEntorno.id] === true;

        if (isSelected) {
          // Buscar si este peligro fue editado en la fase
          const peligroEnFase = fase.peligros.find(
            (p) =>
              p.id === peligroEntorno.id ||
              p.nombre === peligroEntorno.nombre,
          );

          // Si está seleccionado pero no tiene datos completos de barreras
          if (
            !peligroEnFase ||
            !peligroEnFase.barreraFisica?.trim() ||
            !peligroEnFase.barreraAdministrativa?.trim() ||
            !peligroEnFase.eppEspecifico?.trim()
          ) {
            errores.push(
              `Fase ${fase.nroFase} - ${fase.descripcion}: El peligro "${peligroEntorno.nombre}" está seleccionado como "SÍ" pero no tiene todas las barreras de mitigación completas.`,
            );

            logger.log(
              `ERROR - Fase ${fase.nroFase}: Peligro "${peligroEntorno.nombre}" incompleto`,
              {
                peligroEnFase,
                barreraFisica: peligroEnFase?.barreraFisica,
                barreraAdministrativa:
                  peligroEnFase?.barreraAdministrativa,
                eppEspecifico: peligroEnFase?.eppEspecifico,
              },
            );
          }
        }
      });
    });

    if (errores.length > 0) {
      logger.log('Errores encontrados:', errores);
      return false;
    }

    logger.log('Validación de peligros de entorno: OK');
    return true;
  }, [fases, peligroCheckStates, peligrosEntorno]);

  // Exponer la función de validación al componente padre
  React.useEffect(() => {
    logger.log('=== REGISTRANDO FUNCION DE VALIDACION ===');
    logger.log(
      'propsOnValidationRef existe:',
      !!propsOnValidationRef,
    );
    logger.log('validarPeligrosCompletos:', validarPeligrosCompletos);

    if (propsOnValidationRef) {
      logger.log(
        'Registrando función de validación en el componente padre',
      );
      propsOnValidationRef(validarPeligrosCompletos);
    } else {
      logger.log('propsOnValidationRef no está disponible');
    }
  }, [propsOnValidationRef, validarPeligrosCompletos]);

  // Función para crear columnas de la tabla de peligros para una fase específica
  const createColumnsForFase = (
    faseId: number,
  ): ColumnDefinitionType<Peligro, keyof Peligro>[] => {
    const columns: ColumnDefinitionType<Peligro, keyof Peligro>[] =
      [];

    // Columna SI/NO con radio buttons
    columns.push({
      key: 'id',
      header: 'SI / NO',
      cell: (data: Peligro) => (
        <div className="radio-si-no">
          <div className="radio-buttons-row">
            <div className="radio-option">
              <input
                type="radio"
                name={`peligro-${faseId}-${data.id}`}
                checked={
                  peligroCheckStates[faseId]?.[data.id] === true
                }
                onChange={() =>
                  handlePeligroCheckChange(faseId, data.id, true)
                }
                disabled={isDisabled || !isEditing}
              />
            </div>
            <div className="radio-option">
              <input
                type="radio"
                name={`peligro-${faseId}-${data.id}`}
                checked={
                  peligroCheckStates[faseId]?.[data.id] !== true
                }
                onChange={() =>
                  handlePeligroCheckChange(faseId, data.id, false)
                }
                disabled={isDisabled || !isEditing}
              />
            </div>
          </div>
        </div>
      ),
    });

    columns.push({
      key: 'nombre',
      header: 'PELIGRO',
    });

    columns.push({
      key: 'barreraFisica',
      header: 'BARRERA FÍSICA',
    });

    columns.push({
      key: 'barreraAdministrativa',
      header: 'BARRERA ADMINIST.',
    });

    columns.push({
      key: 'eppEspecifico',
      header: 'EPP ESPECÍFICO',
    });

    columns.push({
      key: 'usuarioUltimaEdicion',
      header: 'USUARIO',
    });

    columns.push({
      key: 'id',
      header: 'ACCIONES',
      cell: (data: Peligro) => {
        // Verificar si el peligro está marcado como "SI" (true) para habilitar edición
        const isPeligroSelected =
          peligroCheckStates[faseId]?.[data.id] === true;

        return (
          <>
            <Link
              title={
                isPeligroSelected
                  ? 'Visualizar'
                  : "Seleccione 'SI' para visualizar"
              }
              to="/exportar"
              onClick={(e) => {
                e.preventDefault();
                if (isPeligroSelected) {
                  visualizarPeligro(data);
                }
              }}
              target="_blank"
              style={{
                opacity: isPeligroSelected ? 1 : 0.5,
                pointerEvents: isPeligroSelected ? 'auto' : 'none',
                cursor: isPeligroSelected ? 'pointer' : 'not-allowed',
              }}
            >
              <i
                className={`fa fa-eye me-2 ${
                  isPeligroSelected ? 'text-secondary' : 'text-muted'
                }`}
                aria-hidden="true"
              ></i>
            </Link>
            {isEditing && (
              <Link
                title={
                  isPeligroSelected
                    ? 'Editar'
                    : "Seleccione 'SI' para editar"
                }
                to=""
                onClick={(e) => {
                  e.preventDefault();
                  if (isPeligroSelected) {
                    editarPeligro(data, faseId);
                  }
                }}
                target="_blank"
                style={{
                  opacity: isPeligroSelected ? 1 : 0.5,
                  pointerEvents: isPeligroSelected ? 'auto' : 'none',
                  cursor: isPeligroSelected
                    ? 'pointer'
                    : 'not-allowed',
                }}
              >
                <i
                  className={`fa fa-pencil me-2 ${
                    isPeligroSelected
                      ? 'text-secondary'
                      : 'text-muted'
                  }`}
                  aria-hidden="true"
                ></i>
              </Link>
            )}
          </>
        );
      },
    });

    return columns;
  };

  const handlePeligroCheckChange = (
    faseId: number,
    peligroId: number,
    value: boolean,
  ) => {
    // Si se está cambiando a "NO" (false), verificar si existen datos cargados
    if (value === false) {
      const fase = fases.find((f) => f.id === faseId);
      const peligroEnFase = fase?.peligros.find(
        (p) => p.id === peligroId,
      );

      // Si existe el peligro en la fase y tiene datos cargados en alguna barrera
      if (
        peligroEnFase &&
        (peligroEnFase.barreraFisica?.trim() ||
          peligroEnFase.barreraAdministrativa?.trim() ||
          peligroEnFase.eppEspecifico?.trim())
      ) {
        // Guardar los datos pendientes y mostrar modal de confirmación
        setPendingCheckChange({ faseId, peligroId, value });
        setConfirmModalOpen(true);
        return;
      }
    }

    // Si no hay datos cargados o se está cambiando a "SI", proceder normalmente
    setPeligroCheckStates((prev) => ({
      ...prev,
      [faseId]: {
        ...prev[faseId],
        [peligroId]: value,
      },
    }));
  };

  // Función para confirmar el cambio y limpiar los datos
  const handleConfirmCheckChange = () => {
    if (pendingCheckChange) {
      const { faseId, peligroId, value } = pendingCheckChange;

      // Limpiar los datos del peligro en la fase
      const nuevasFases = fases.map((f) => {
        if (f.id === faseId) {
          return {
            ...f,
            peligros: f.peligros.map((p) =>
              p.id === peligroId
                ? {
                    ...p,
                    barreraFisica: '',
                    barreraAdministrativa: '',
                    eppEspecifico: '',
                  }
                : p,
            ),
          };
        }
        return f;
      });

      setFasesEntorno(nuevasFases);

      // Actualizar el estado del checkbox
      setPeligroCheckStates((prev) => ({
        ...prev,
        [faseId]: {
          ...prev[faseId],
          [peligroId]: value,
        },
      }));
    }

    // Cerrar modal y limpiar estado pendiente
    setConfirmModalOpen(false);
    setPendingCheckChange(null);
  };

  // Función para cancelar el cambio
  const handleCancelCheckChange = () => {
    setConfirmModalOpen(false);
    setPendingCheckChange(null);
  };

  const handleAgregarFase = () => {
    agregarFase(nuevaFase, 'ENTORNO');
    setNuevaFase('');
  };

  const handleConfirmDeleteFase = () => {
    if (faseIdToDelete !== undefined) {
      eliminarFase(faseIdToDelete, 'ENTORNO');
    }
    setIsConfirmDeleteFase(false);
    setFaseIdToDelete(undefined);
  };

  const editarPeligro = (peligro: Peligro, faseId: number) => {
    // Si el peligro no tiene barreras definidas, significa que viene de la tabla y necesitamos agregarlo primero
    const fase = fases.find((f) => f.id === faseId);
    const peligroEnFase = fase?.peligros.find(
      (p) => p.id === peligro.id || p.nombre === peligro.nombre,
    );

    if (!peligroEnFase) {
      // Buscar el peligro original del backend para mantener el ID correcto
      const peligroOriginal = peligrosEntorno.find(
        (p) => p.id === peligro.id || p.nombre === peligro.nombre,
      );

      if (peligroOriginal) {
        // Agregar el peligro a la fase primero manteniendo el ID original
        agregarPeligroDesdeTabla(faseId, peligroOriginal);
      }
    }

    handlePeligroSelected(peligro);
    setCurrentFaseId(faseId);
    setPeligroFormData({
      nombre: peligro.nombre,
      barreraAdministrativa: peligro.barreraAdministrativa || '',
      barreraFisica: peligro.barreraFisica || '',
      eppEspecifico: peligro.eppEspecifico || '',
    });
    setIsVisualizar(false);
    setModalOpen(true);
  };

  const visualizarPeligro = (peligro: Peligro) => {
    handlePeligroSelected(peligro);
    setIsVisualizar(true);
    setModalOpen(true);
  };

  const handleFormChange = (field: string, value: string) => {
    setPeligroFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Limpiar error cuando el usuario empiece a escribir
    if (formErrors[field as keyof typeof formErrors]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  // Función para validar el formulario
  const validateForm = (): boolean => {
    const errors = {
      nombre: '',
      barreraAdministrativa: '',
      barreraFisica: '',
      eppEspecifico: '',
    };

    let isValid = true;

    // Validar que el peligro esté seleccionado
    if (!peligroFormData.nombre.trim()) {
      errors.nombre = 'Debe seleccionar un peligro';
      isValid = false;
    }

    // Validar barrera administrativa
    if (!peligroFormData.barreraAdministrativa.trim()) {
      errors.barreraAdministrativa =
        'La barrera administrativa es obligatoria';
      isValid = false;
    }

    // Validar barrera física
    if (!peligroFormData.barreraFisica.trim()) {
      errors.barreraFisica = 'La barrera física es obligatoria';
      isValid = false;
    }

    // Validar EPP específico
    if (!peligroFormData.eppEspecifico.trim()) {
      errors.eppEspecifico = 'El EPP específico es obligatorio';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSavePeligro = () => {
    // Validar el formulario antes de guardar
    if (!validateForm()) {
      return;
    }

    if (currentFaseId && peligroFormData.nombre.trim()) {
      const fase = fases.find((f) => f.id === currentFaseId);
      const peligroEnFase = fase?.peligros.find(
        (p) => p.nombre === peligroFormData.nombre,
      );

      // Buscar el peligro original del backend para obtener el ID correcto
      const peligroOriginal = peligrosEntorno.find(
        (p) => p.nombre === peligroFormData.nombre,
      );

      const nuevasFases = fases.map((f) => {
        if (f.id === currentFaseId) {
          if (peligroEnFase) {
            // Editar peligro existente en la fase
            return {
              ...f,
              peligros: f.peligros.map((p) =>
                p.id === peligroEnFase.id
                  ? {
                      ...p,
                      nombre: peligroFormData.nombre,
                      barreraAdministrativa:
                        peligroFormData.barreraAdministrativa,
                      barreraFisica: peligroFormData.barreraFisica,
                      eppEspecifico: peligroFormData.eppEspecifico,
                    }
                  : p,
              ),
            };
          } else {
            // Agregar nuevo peligro a la fase manteniendo el ID original
            const nuevoPeligro: Peligro = {
              id: peligroOriginal?.id || f.peligros.length + 1, // Usar ID original o generar uno nuevo
              nombre: peligroFormData.nombre,
              barreraAdministrativa:
                peligroFormData.barreraAdministrativa,
              barreraFisica: peligroFormData.barreraFisica,
              eppEspecifico: peligroFormData.eppEspecifico,
            };
            return {
              ...f,
              peligros: [...f.peligros, nuevoPeligro],
            };
          }
        }
        return f;
      });

      setFasesEntorno(nuevasFases);
      handleCloseModal();
    }
  };

  // Función para agregar un peligro desde la tabla a la fase
  const agregarPeligroDesdeTabla = (
    faseId: number,
    peligroEntorno: PeligroDTO,
  ) => {
    // Verificar si el peligro ya existe en la fase
    const fase = fases.find((f) => f.id === faseId);
    const peligroExiste = fase?.peligros.find(
      (p) =>
        p.id === peligroEntorno.id ||
        p.nombre === peligroEntorno.nombre,
    );

    if (!peligroExiste) {
      // Agregar el peligro directamente a las fases de entorno manteniendo el ID original
      const nuevasFases = fases.map((f) => {
        if (f.id === faseId) {
          const nuevoPeligro: Peligro = {
            id: peligroEntorno.id, // Mantener el ID original del backend
            nombre: peligroEntorno.nombre,
            barreraAdministrativa: '',
            barreraFisica: '',
            eppEspecifico: '',
          };
          return {
            ...f,
            peligros: [...f.peligros, nuevoPeligro],
          };
        }
        return f;
      });
      setFasesEntorno(nuevasFases);
    }
  };

  // Función para obtener los datos que se muestran en la tabla
  const getTableDataForFase = (faseId: number) => {
    const fase = fases.find((f) => f.id === faseId);

    return peligrosEntorno.map((peligroEntorno: PeligroDTO) => {
      // Buscar si este peligro ya fue agregado/editado en esta fase
      const peligroEnFase = fase?.peligros.find(
        (p) =>
          p.id === peligroEntorno.id ||
          p.nombre === peligroEntorno.nombre,
      );

      if (peligroEnFase) {
        // Si existe en la fase, mostrar los datos editados pero mantener el ID original
        return {
          id: peligroEntorno.id, // Usar siempre el ID original del backend
          nombre: peligroEnFase.nombre,
          barreraAdministrativa: peligroEnFase.barreraAdministrativa,
          barreraFisica: peligroEnFase.barreraFisica,
          eppEspecifico: peligroEnFase.eppEspecifico,
          usuarioUltimaEdicion:
            peligroEnFase.usuarioUltimaEdicion ?? '',
          peligroOriginalId: peligroEntorno.id, // Para mantener referencia al peligro del backend
        };
      } else {
        // Si no existe en la fase, mostrar datos vacíos pero mantener el peligro disponible
        return {
          id: peligroEntorno.id,
          nombre: peligroEntorno.nombre,
          barreraAdministrativa: '',
          barreraFisica: '',
          eppEspecifico: '',
          usuarioUltimaEdicion: '',
          peligroOriginalId: peligroEntorno.id,
        };
      }
    });
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setIsVisualizar(false);
    setPeligroFormData({
      nombre: '',
      barreraAdministrativa: '',
      barreraFisica: '',
      eppEspecifico: '',
    });
    setFormErrors({
      nombre: '',
      barreraAdministrativa: '',
      barreraFisica: '',
      eppEspecifico: '',
    });
  };

  return (
    <>
      {/* Modal para ver/editar peligro */}
      <MyModal
        size="lg"
        isOpen={modalOpen}
        toggle={handleCloseModal}
        cancelButton={true}
        secondButton={!isVisualizar}
        secondButtonTitle={'Aceptar'}
        secondButtonFn={handleSavePeligro}
        title={
          isVisualizar
            ? 'Vista Peligro'
            : peligroSelected.id === 0
              ? 'Agregar Peligro'
              : 'Editar Peligro'
        }
      >
        <div className="container-fluid modal-peligro-form">
          {isVisualizar ? (
            // Vista para visualizar peligro - mismo formulario pero deshabilitado
            <div>
              <div className="row mb-4">
                <FormSelectBox
                  title={'Peligro'}
                  label={'peligro'}
                  id={'peligro-select-view'}
                  name={'peligro-select-view'}
                  optionId={'nombre'}
                  optionLabel={'nombre'}
                  value={peligroSelected.nombre}
                  options={peligrosEntorno}
                  isDisabled={true}
                  classLabel={'col-form-label col-3 text-end'}
                  classDivSelect={'col-9 select-peligro'}
                  onChange={() => {}}
                />
              </div>

              <div className="barrera-mitigacion">
                <h6 className="mb-3">Barrera de Mitigación</h6>

                <div className="row mb-3">
                  <FormInput
                    id={'barrera-administrativa-view'}
                    label={'Barrera Administrativa'}
                    name={'barrera-administrativa-view'}
                    value={peligroSelected.barreraAdministrativa}
                    onChange={() => {}}
                    placeholder={''}
                    isDisabled={true}
                    classLabel={'col-form-label col-4 text-end'}
                    classColumn={'col-8'}
                  />
                </div>

                <div className="row mb-3">
                  <FormInput
                    id={'barrera-fisica-view'}
                    label={'Barrera Física'}
                    name={'barrera-fisica-view'}
                    value={peligroSelected.barreraFisica}
                    onChange={() => {}}
                    placeholder={''}
                    isDisabled={true}
                    classLabel={'col-form-label col-4 text-end'}
                    classColumn={'col-8'}
                  />
                </div>

                <div className="row mb-3">
                  <FormInput
                    id={'epp-especifico-view'}
                    label={'EPP Específico'}
                    name={'epp-especifico-view'}
                    value={peligroSelected.eppEspecifico}
                    onChange={() => {}}
                    placeholder={''}
                    isDisabled={true}
                    classLabel={'col-form-label col-4 text-end'}
                    classColumn={'col-8'}
                  />
                </div>
              </div>
            </div>
          ) : (
            // Formulario para agregar/editar peligro
            <div>
              <div className="row mb-4">
                <FormSelectBox
                  title={'Peligro *'}
                  label={'peligro'}
                  id={'peligro-select'}
                  name={'peligro-select'}
                  optionId={'nombre'}
                  optionLabel={'nombre'}
                  value={peligroFormData.nombre}
                  options={peligrosEntorno}
                  isDisabled={peligroSelected.id !== 0} // Deshabilitar si estamos editando (id !== 0)
                  classLabel={'col-form-label col-3 text-end'}
                  classDivSelect={'col-9 select-peligro'}
                  onChange={(e) =>
                    handleFormChange('nombre', e.target.value)
                  }
                />
                {formErrors.nombre && (
                  <div className="col-9 offset-3">
                    <small className="text-danger">
                      {formErrors.nombre}
                    </small>
                  </div>
                )}
              </div>

              <div className="barrera-mitigacion">
                <h6 className="mb-3">Barrera de Mitigación</h6>
                <div className="row mb-3">
                  <FormInput
                    id={'barrera-fisica'}
                    label={'Barrera Física *'}
                    name={'barrera-fisica'}
                    value={peligroFormData.barreraFisica}
                    onChange={(e) =>
                      handleFormChange(
                        'barreraFisica',
                        e.target.value,
                      )
                    }
                    placeholder={'Ingrese la barrera física'}
                    isDisabled={false}
                    classLabel={'col-form-label col-4 text-end'}
                    classColumn={'col-8'}
                    maxLength={500}
                  />
                  {formErrors.barreraFisica && (
                    <div className="col-8 offset-4">
                      <small className="text-danger">
                        {formErrors.barreraFisica}
                      </small>
                    </div>
                  )}
                </div>
                <div className="row mb-3">
                  <FormInput
                    id={'barrera-administrativa'}
                    label={'Barrera Administrativa *'}
                    name={'barrera-administrativa'}
                    value={peligroFormData.barreraAdministrativa}
                    onChange={(e) =>
                      handleFormChange(
                        'barreraAdministrativa',
                        e.target.value,
                      )
                    }
                    placeholder={'Ingrese la barrera administrativa'}
                    isDisabled={false}
                    classLabel={'col-form-label col-4 text-end'}
                    classColumn={'col-8'}
                    maxLength={500}
                  />
                  {formErrors.barreraAdministrativa && (
                    <div className="col-8 offset-4">
                      <small className="text-danger">
                        {formErrors.barreraAdministrativa}
                      </small>
                    </div>
                  )}
                </div>

                <div className="row mb-3">
                  <FormInput
                    id={'epp-especifico'}
                    label={'EPP Específico *'}
                    name={'epp-especifico'}
                    value={peligroFormData.eppEspecifico}
                    onChange={(e) =>
                      handleFormChange(
                        'eppEspecifico',
                        e.target.value,
                      )
                    }
                    placeholder={'Ingrese el EPP específico'}
                    isDisabled={false}
                    classLabel={'col-form-label col-4 text-end'}
                    classColumn={'col-8'}
                    maxLength={500}
                  />
                  {formErrors.eppEspecifico && (
                    <div className="col-8 offset-4">
                      <small className="text-danger">
                        {formErrors.eppEspecifico}
                      </small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </MyModal>

      {/* Modal de confirmación para eliminar fase */}
      <MyModal
        isOpen={isConfirmDeleteFase}
        toggle={() => setIsConfirmDeleteFase(false)}
        title="Advertencia"
        cancelButton={true}
        secondButton={true}
        secondButtonTitle={'Aceptar'}
        secondButtonFn={handleConfirmDeleteFase}
      >
        <label>
          Se perderá toda la información de la fase. ¿Confirma la
          eliminación?
        </label>
      </MyModal>

      {/* Modal de confirmación para limpiar datos */}
      <MyModal
        size="sm"
        isOpen={confirmModalOpen}
        toggle={handleCancelCheckChange}
        cancelButton={true}
        secondButton={true}
        secondButtonTitle="Confirmar"
        secondButtonFn={handleConfirmCheckChange}
        title="Confirmar acción"
      >
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <p>
                Si indica que el peligro NO aplica en esta fase se
                perderá la información ingresada como Barrera de
                Mitigación.
              </p>
            </div>
          </div>
        </div>
      </MyModal>

      <div className="evaluacion-riesgos-container">
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">
              Identificación de Peligros de Entorno
            </h5>
            {isEditing && (
              <div className="d-flex gap-2">
                <Button
                  className="btn-primary"
                  variant="primary"
                  onClick={handleAgregarFase}
                  disabled={isDisabled}
                >
                  Agregar Fase
                </Button>
              </div>
            )}
          </div>

          {/* Sección de Fases */}
          {fases.map((fase) => (
            <div key={fase.id} className="mb-4 fase-container p-3">
              <div className="row mb-3 align-items-end">
                <div className="col-md-10">
                  <div className="d-flex align-items-end gap-2">
                    <div className="fase-numero-container">
                      <label className="form-label">FASE</label>
                      <FormInput
                        id={`fase-${fase.id}`}
                        label={''}
                        name={`fase-${fase.id}`}
                        value={fase.nroFase.toString()}
                        isDisabled={true}
                        classColumn={'fase-numero-input'}
                      />
                    </div>
                    <div
                      className="flex-grow-1"
                      title={
                        fase.descripcion.length > 100
                          ? fase.descripcion
                          : ''
                      }
                      style={{
                        cursor:
                          fase.descripcion.length > 100
                            ? // && (isDisabled || !isEditing)
                              'help'
                            : 'auto',
                      }}
                    >
                      <FormInput
                        id={`descripcion-${fase.id}`}
                        label={''}
                        name={`descripcion-${fase.id}`}
                        placeholder="Descripción de la fase"
                        value={fase.descripcion}
                        onChange={(e) =>
                          editarFase(
                            fase.id,
                            e.target.value,
                            'ENTORNO',
                          )
                        }
                        maxLength={500}
                        isDisabled={isDisabled || !isEditing}
                        classColumn={''}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-md-2 text-end">
                  {isEditing && !isDisabled && (
                    <Button
                      variant="danger"
                      onClick={() => {
                        setFaseIdToDelete(fase.id);
                        setIsConfirmDeleteFase(true);
                      }}
                      size="sm"
                    >
                      Eliminar Fase
                    </Button>
                  )}
                </div>
              </div>

              {/* Tabla de Peligros con datos del backend */}
              <div className="table-responsive">
                {loadingPeligrosEntorno ? (
                  <div className="text-center p-3">
                    <span>Cargando peligros...</span>
                  </div>
                ) : (
                  <Table
                    data={getTableDataForFase(fase.id)}
                    columns={createColumnsForFase(fase.id)}
                  ></Table>
                )}
              </div>
            </div>
          ))}

          {/* Sección de Observaciones Generales */}
          <div className="mt-4">
            <div className="observaciones-container p-3">
              <h6 className="mb-3">
                Observaciones Generales Análisis de Riesgo
              </h6>
              <div className="observaciones-text-area">
                <textarea
                  className="form-control"
                  rows={4}
                  placeholder="Escriba aquí las observaciones generales del análisis de riesgo..."
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  disabled={isDisabled || !isEditing}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default IdentificacionPeligrosEntorno;
